import React, { CSSProperties, ElementType, FC, useRef, useState } from "react";
import cn from "clsx";
import { Link, LinkProps } from "react-router-dom";
import { BANNER_ROW_TYPE } from "@/constants";
import DynamicVideo from "../dynamic-video";
import { isMobile } from "@/util";
import { isLinkStartsFromHTTPS } from "@/util/common";
import BannerIntersectionObserver from "../../banner/bannerIntersectionObserver";
import BannerImageComponent from "./banner-image";
import { BannerImage } from "@/routes/catalogPage/types";
import { useLongLanguage, useRouteSlug } from "@/hooks";

type DynamicSingleBannerProps = {
  data: BannerImage;
  imageListLength: number;
  index: number;
  bannerModuleName: string;
  rowTitle: string;
  bannerPageTitle: string;
};

type CommonProps = {
  className?: string;
  "data-banner-link"?: string;
  "data-banner-index"?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  target: "_blank" | "_self";
  style: CSSProperties;
  children?: React.ReactNode;
};

type ExternalLinkProps = CommonProps & {
  isExternalLink: true;
  redirectionPath: string;
};

type InternalLinkProps = CommonProps & {
  isExternalLink: false;
  redirectionPath: LinkProps["to"];
};

type BannerLinkProps = ExternalLinkProps | InternalLinkProps;

const BannerLink: FC<BannerLinkProps> = ({
  isExternalLink,
  redirectionPath,
  ...restProps
}) => {
  const Component: ElementType = isExternalLink ? "a" : Link;
  const linkProps = isExternalLink
    ? { href: redirectionPath }
    : { to: redirectionPath };
  return <Component {...restProps} {...linkProps} />;
};

const stylesForSingleRow = (
  { hover, ...restStyles },
  { hovered, imageWidth, marginForImage, hoverValues }
) => ({
  ...restStyles,
  ...(!isMobile.any() && hovered && hoverValues && hover),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flex: `0 0 calc(${imageWidth}% - ${marginForImage}px)`
});

const DynamicSingleBanner: FC<DynamicSingleBannerProps> = ({
  data,
  imageListLength,
  index,
  bannerModuleName,
  rowTitle,
  bannerPageTitle
}) => {
  const language = useLongLanguage();
  const imageLinkRef = useRef(null);
  const routeSlug = useRouteSlug();
  const [hovered, setHovered] = useState(false);
  const { styles } = data;
  const languageSpecifiedData = data[language];
  if (!languageSpecifiedData) return null;
  const { redirectionLink, imageURL, ratio } = languageSpecifiedData;
  const imageWidth = 100 / imageListLength;
  const hoverValues = styles.hoverValues?.transform;
  const marginForImage = styles.marginLeft + styles.marginRight || 0;
  const isExternalLink =
    redirectionLink && isLinkStartsFromHTTPS(redirectionLink);
  const redirectionPath = isExternalLink
    ? redirectionLink
    : `/${routeSlug}${redirectionLink || "/"}`;
  const linkTarget = isExternalLink ? "_blank" : "_self";
  const dataForAnalytic = {
    bannerModuleName: bannerModuleName?.toUpperCase(),
    bannerRowName: rowTitle?.toUpperCase(),
    index: index + 1,
    imageURL: imageURL || languageSpecifiedData.videoUrl,
    pageName: bannerPageTitle,
    imageListData: data
  };

  if (data.type === BANNER_ROW_TYPE.VIDEO) {
    return (
      <DynamicVideo
        data={languageSpecifiedData}
        imageWidth={imageWidth}
        marginForImage={marginForImage}
        styles={styles}
        linkTarget={linkTarget}
        redirectionPath={redirectionPath}
        index={index}
        dataForAnalytic={dataForAnalytic}
        imagePlaceholderType="SINGLE_BANNER"
      />
    );
  }

  const linkProps: BannerLinkProps = {
    className: cn({
      "disabled-link": !redirectionLink
    }),
    target: linkTarget as "_blank" | "_self",
    style: stylesForSingleRow(styles, {
      hovered,
      imageWidth,
      marginForImage,
      hoverValues
    }),
    "data-banner-link": redirectionLink,
    "data-banner-index": index,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    isExternalLink,
    redirectionPath
  };

  return (
    <BannerIntersectionObserver data={dataForAnalytic}>
      <BannerLink {...linkProps}>
        <div className="banner-element">
          <div
            className="banner-child"
            ref={imageLinkRef}
            style={{ paddingBottom: `${ratio}%` }}
          >
            <BannerImageComponent
              imageURL={imageURL}
              onImageLoad={() => {
                if (imageLinkRef.current) {
                  imageLinkRef.current.style.background = "none";
                }
              }}
            />
          </div>
        </div>
      </BannerLink>
    </BannerIntersectionObserver>
  );
};

export default DynamicSingleBanner;
